import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Result,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd';
import {
  CheckCircleOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  EyeOutlined,
  LeftOutlined,
  ReloadOutlined,
  RightOutlined,
  SearchOutlined,
  UserSwitchOutlined
} from '@ant-design/icons';
import { Navigate, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import API from '../../API/API';
import { ROUTES } from '../../Routes/Routes';
import LeadFullDetailModal from '../../Components/LeadFullDetailModal.jsx';
import LeadLocalTime from '../../Components/LeadLocalTime/LeadLocalTime.jsx';

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const reasonOptions = [
  'PHONE_FORMAT_INVALID',
  'PHONE_NON_USA',
  'TWILIO_BASIC_INVALID',
  'TWILIO_LINE_TYPE_UNKNOWN',
  'TWILIO_LINE_TYPE_NON_FIXED_VOIP',
  'TWILIO_LINE_STATUS_UNKNOWN',
  'TWILIO_LINE_INACTIVE',
  'TIMEZONE_MISMATCH'
];

const reviewTableShellStyle = {
  position: 'relative',
  maxWidth: '100%',
};

const reviewTableScrollControlsStyle = {
  position: 'sticky',
  top: 88,
  zIndex: 12,
  height: 0,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingLeft: 56,
  paddingRight: 206,
  pointerEvents: 'none',
};

const reviewTableScrollButtonStyle = {
  pointerEvents: 'auto',
  width: 36,
  height: 36,
  minWidth: 36,
  borderRadius: '50%',
  boxShadow: '0 8px 20px rgba(15, 23, 42, 0.18)',
};

const reviewTableScrollStep = 520;

const phoneReasonCodeTagStyle = {
  maxWidth: '100%',
  marginTop: 4,
  marginInlineEnd: 0,
  whiteSpace: 'normal',
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
  lineHeight: '18px',
};

const phoneReasonTextStyle = {
  display: 'block',
  maxWidth: '100%',
  fontSize: 12,
  whiteSpace: 'normal',
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
};

const getTodayDateRange = () => {
  const today = dayjs();
  return [today, today];
};

const getThisMonthDateRange = () => {
  const today = dayjs();
  return [today.startOf('month'), today.endOf('month')];
};

const reviewLeadDateRangePresets = [
  {
    label: 'Today',
    value: getTodayDateRange
  },
  {
    label: 'Yesterday',
    value: () => {
      const yesterday = dayjs().subtract(1, 'day');
      return [yesterday, yesterday];
    }
  },
  {
    label: "This Week's Leads",
    value: () => {
      const today = dayjs();
      return [today.startOf('week'), today.endOf('week')];
    }
  },
  {
    label: 'Last 7 Days',
    value: () => {
      const today = dayjs();
      return [today.subtract(6, 'day'), today];
    }
  },
  {
    label: 'Last 30 Days',
    value: () => {
      const today = dayjs();
      return [today.subtract(29, 'day'), today];
    }
  },
  {
    label: 'This Month',
    value: getThisMonthDateRange
  },
  {
    label: 'Last Month',
    value: () => {
      const lastMonth = dayjs().subtract(1, 'month');
      return [lastMonth.startOf('month'), lastMonth.endOf('month')];
    }
  }
];

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem('userData') || '{}');
  } catch {
    return {};
  }
}

function formatDate(value) {
  return value ? dayjs(value).format('MM-DD-YYYY hh:mm A') : 'N/A';
}

function getUserDisplayName(user) {
  if (!user || typeof user !== 'object') {
    return '-';
  }

  const fullName = [user.firstName, user.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  return fullName || user.userName || user.email || '-';
}

function getVehicleText(vehicleInfo = {}) {
  const yearMakeModel = [vehicleInfo.year, vehicleInfo.make, vehicleInfo.model]
    .filter(Boolean)
    .join(' ');
  const part = vehicleInfo.part || 'N/A';
  const vin = vehicleInfo.vin || vehicleInfo.vinNumber;

  return (
    <Space direction="vertical" size={0}>
      <Text>{yearMakeModel || 'N/A'}</Text>
      <Text type="secondary" style={{ fontSize: 12 }}>Part: {part}</Text>
      {vin && <Text type="secondary" style={{ fontSize: 12 }}>VIN: {vin}</Text>}
    </Space>
  );
}

function ReviewLeads() {
  const navigate = useNavigate();
  const userData = useMemo(() => getStoredUser(), []);
  const canReviewLeads = userData?.role === 'ADMIN';

  const [reviewLeads, setReviewLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState(getThisMonthDateRange);
  const [reasonCode, setReasonCode] = useState(undefined);
  const [transferStatus, setTransferStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [totalRecords, setTotalRecords] = useState(0);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedReviewLead, setSelectedReviewLead] = useState(null);
  const [recoverOpen, setRecoverOpen] = useState(false);
  const [recoverLoading, setRecoverLoading] = useState(false);
  const [salesAgents, setSalesAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [fullDetailLeadId, setFullDetailLeadId] = useState(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [tableScrollState, setTableScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false
  });
  const tableShellRef = useRef(null);
  const [recoverForm] = Form.useForm();

  const handleDateRangeChange = useCallback((dates) => {
    setCurrentPage(1);
    setDateRange(dates?.[0] && dates?.[1] ? dates : getThisMonthDateRange());
  }, []);

  const handleRefreshReviewLeads = useCallback(() => {
    setCurrentPage(1);
    setDateRange(getThisMonthDateRange());
  }, []);

  const getTableScrollElement = useCallback(() => {
    if (!tableShellRef.current) return null;

    return (
      tableShellRef.current.querySelector('.ant-table-content')
      || tableShellRef.current.querySelector('.ant-table-body')
    );
  }, []);

  const updateTableScrollState = useCallback(() => {
    const scrollElement = getTableScrollElement();

    if (!scrollElement) {
      setTableScrollState((previousState) => (
        previousState.canScrollLeft || previousState.canScrollRight
          ? { canScrollLeft: false, canScrollRight: false }
          : previousState
      ));
      return;
    }

    const maxScrollLeft = scrollElement.scrollWidth - scrollElement.clientWidth;
    const canScrollLeft = scrollElement.scrollLeft > 1;
    const canScrollRight = maxScrollLeft > 1 && scrollElement.scrollLeft < maxScrollLeft - 1;

    setTableScrollState((previousState) => (
      previousState.canScrollLeft === canScrollLeft && previousState.canScrollRight === canScrollRight
        ? previousState
        : { canScrollLeft, canScrollRight }
    ));
  }, [getTableScrollElement]);

  const scrollTableHorizontally = useCallback((direction) => {
    const scrollElement = getTableScrollElement();
    if (!scrollElement) return;

    scrollElement.scrollBy({
      left: direction === 'right' ? reviewTableScrollStep : -reviewTableScrollStep,
      behavior: 'smooth'
    });

    window.setTimeout(updateTableScrollState, 350);
  }, [getTableScrollElement, updateTableScrollState]);

  useEffect(() => {
    const scrollElement = getTableScrollElement();

    updateTableScrollState();

    if (!scrollElement) return undefined;

    scrollElement.addEventListener('scroll', updateTableScrollState, { passive: true });
    window.addEventListener('resize', updateTableScrollState);

    const resizeObserver = typeof window !== 'undefined' && window.ResizeObserver
      ? new window.ResizeObserver(updateTableScrollState)
      : null;

    resizeObserver?.observe(scrollElement);
    if (scrollElement.firstElementChild) {
      resizeObserver?.observe(scrollElement.firstElementChild);
    }

    return () => {
      scrollElement.removeEventListener('scroll', updateTableScrollState);
      window.removeEventListener('resize', updateTableScrollState);
      resizeObserver?.disconnect();
    };
  }, [getTableScrollElement, loading, reviewLeads.length, updateTableScrollState]);

  const fetchReviewLeads = useCallback(async () => {
    if (!canReviewLeads) return;

    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: pageSize
      };

      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (reasonCode) params.reasonCode = reasonCode;
      if (transferStatus !== 'all') params.transferStatus = transferStatus;
      if (dateRange?.[0] && dateRange?.[1]) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }

      const response = await API.getReviewLeads(params);

      if (response?.status) {
        setReviewLeads(response.data || []);
        setTotalRecords(response.pagination?.totalRecords || 0);
      } else {
        setReviewLeads([]);
        setTotalRecords(0);
        message.error(response?.message || 'Failed to fetch Review Leads.');
      }
    } catch (error) {
      console.error('Failed to fetch Review Leads:', error);
      setReviewLeads([]);
      setTotalRecords(0);
      message.error('Failed to fetch Review Leads.');
    } finally {
      setLoading(false);
    }
  }, [canReviewLeads, currentPage, dateRange, pageSize, reasonCode, searchTerm, transferStatus]);

  const fetchSalesAgents = useCallback(async () => {
    setAgentsLoading(true);
    try {
      const response = await API.userGetAll();
      if (response?.success || response?.status) {
        const activeSalesAgents = (response.data || [])
          .filter((user) => user.role === 'SALES-AGENT' && user.isActive && !user.isDeleted)
          .map((user) => ({
            value: user._id,
            label: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.userName || user.email,
            subRole: user.subRole,
            email: user.email
          }));
        setSalesAgents(activeSalesAgents);
      } else {
        setSalesAgents([]);
        message.error(response?.message || 'Failed to load Sales Agents.');
      }
    } catch (error) {
      console.error('Failed to load Sales Agents:', error);
      setSalesAgents([]);
      message.error('Failed to load Sales Agents.');
    } finally {
      setAgentsLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(fetchReviewLeads, 400);
    return () => clearTimeout(timer);
  }, [fetchReviewLeads]);

  useEffect(() => {
    if (canReviewLeads) {
      fetchSalesAgents();
    }
  }, [canReviewLeads, fetchSalesAgents]);

  const openDetails = async (record) => {
    setSelectedReviewLead(record);
    setDetailsOpen(true);
    setDetailsLoading(true);

    try {
      const response = await API.getReviewLeadById(record._id);
      if (response?.status) {
        setSelectedReviewLead(response.data);
      } else {
        message.error(response?.message || 'Failed to load Review Lead details.');
      }
    } catch (error) {
      console.error('Failed to load Review Lead details:', error);
      message.error('Failed to load Review Lead details.');
    } finally {
      setDetailsLoading(false);
    }
  };

  const openRecover = (record) => {
    if (record.recoveredLeadId) {
      message.warning('This Review Lead has already been recovered.');
      return;
    }

    setSelectedReviewLead(record);
    recoverForm.resetFields();
    setRecoverOpen(true);
  };

  const submitRecovery = async (values) => {
    if (!selectedReviewLead?._id) return;

    setRecoverLoading(true);
    try {
      const response = await API.recoverReviewLead(selectedReviewLead._id, {
        targetAgentId: values.targetAgentId,
        reason: values.reason || ''
      });

      if (response?.status || response?.success) {
        message.success(response?.message || 'Review Lead recovered successfully.');
        setRecoverOpen(false);
        setSelectedReviewLead(null);
        recoverForm.resetFields();
        fetchReviewLeads();
      } else {
        message.error(response?.message || 'Failed to recover Review Lead.');
      }
    } catch (error) {
      console.error('Failed to recover Review Lead:', error);
      message.error('Failed to recover Review Lead.');
    } finally {
      setRecoverLoading(false);
    }
  };

  const handleSingleDelete = async (record) => {
    setDeletingId(record._id);
    try {
      const response = await API.deleteReviewLead(record._id);

      if (response?.status || response?.success) {
        message.success(response?.message || 'Review Lead deleted.');
        setSelectedRowKeys((keys) => keys.filter((key) => key !== record._id));
        fetchReviewLeads();
      } else {
        message.error(response?.message || 'Failed to delete Review Lead.');
      }
    } catch (error) {
      console.error('Failed to delete Review Lead:', error);
      message.error('Failed to delete Review Lead.');
    } finally {
      setDeletingId(null);
    }
  };

  const confirmBulkDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select Review Leads to delete.');
      return;
    }

    Modal.confirm({
      title: 'Delete selected Review Leads?',
      content: 'Are you sure you want to permanently delete selected Review Leads? This will not delete any recovered Leads.',
      okText: 'Delete',
      cancelText: 'Cancel',
      okType: 'danger',
      onOk: async () => {
        setBulkDeleting(true);
        try {
          const response = await API.bulkDeleteReviewLeads(selectedRowKeys);

          if (response?.status || response?.success) {
            message.success(response?.message || 'Selected Review Leads deleted.');
            setSelectedRowKeys([]);
            fetchReviewLeads();
          } else {
            message.error(response?.message || 'Failed to delete selected Review Leads.');
          }
        } catch (error) {
          console.error('Failed to bulk delete Review Leads:', error);
          message.error('Failed to delete selected Review Leads.');
        } finally {
          setBulkDeleting(false);
        }
      }
    });
  };

  if (!userData?.id && !userData?._id) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (!canReviewLeads) {
    return (
      <Result
        status="403"
        title="Access Denied"
        subTitle="Only Admin users can view Review Leads."
        extra={(
          <Button type="primary" onClick={() => navigate(ROUTES.MYLEADS)}>
            Back to Leads
          </Button>
        )}
      />
    );
  }

  const columns = [
    {
      title: 'Lead',
      dataIndex: ['personalInfo', 'fullName'],
      key: 'fullName',
      width: 220,
      render: (value, record) => (
        <Space direction="vertical" size={2}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Text>{value || 'N/A'}</Text>
            <LeadLocalTime
              zipCode={record.personalInfo?.zipCode}
              browserTimeZone={record.browserInfo?.timeZone}
            />
          </span>
          {record.isExistingCustomer === true && (
            <Tag color="green" style={{ marginInlineEnd: 0 }}>
              Existing Customer
            </Tag>
          )}
          {record.isExistingCustomer === true && record.previousSalesAgent?.name && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Previous Sales Agent: <Text style={{ fontSize: 12, color: '#4096ff' }}>{record.previousSalesAgent.name}</Text>
            </Text>
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.personalInfo?.email || 'N/A'}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Source: {record.source || 'unknown'}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Created At: {formatDate(record.createdAt)}
          </Text>
        </Space>
      )
    },
    {
      title: 'Phone & Reason',
      dataIndex: ['personalInfo', 'phoneNumber'],
      key: 'phoneNumber',
      width: 220,
      render: (value, record) => (
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          <Text>{value || 'N/A'}</Text>
          {record.phoneValidation?.normalizedE164 && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.phoneValidation.normalizedE164}
            </Text>
          )}
          <Tag color="red" style={phoneReasonCodeTagStyle}>
            {record.reasonCode || 'N/A'}
          </Tag>
          <Text type="secondary" style={phoneReasonTextStyle}>
            {record.reason || 'N/A'}
          </Text>
        </Space>
      )
    },
    {
      title: 'Vehicle Info',
      dataIndex: 'vehicleInfo',
      key: 'vehicleInfo',
      width: 240,
      render: getVehicleText
    },
    {
      title: 'Transfer Status',
      key: 'transferStatus',
      width: 200,
      render: (_, record) => (
        record.recoveredLeadId ? (
          <Space direction="vertical" size={2}>
            <Tag color="success" icon={<CheckCircleOutlined />} style={{ marginInlineEnd: 0 }}>
              Transferred
            </Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {formatDate(record.reviewedAt)}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              By: {getUserDisplayName(record.reviewedBy)}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              To: {getUserDisplayName(record.assignedTo)}
            </Text>
          </Space>
        ) : (
          <Tag>Not Transferred</Tag>
        )
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 230,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={(event) => {
                event.stopPropagation();
                openDetails(record);
              }}
            />
          </Tooltip>
          <Tooltip title="View More Detail">
            <Button
              type="text"
              icon={<DatabaseOutlined />}
              onClick={(event) => {
                event.stopPropagation();
                setFullDetailLeadId(record._id);
              }}
            />
          </Tooltip>
          {record.recoveredLeadId ? (
            <Tooltip title="Lead transferred">
              <Tag color="success" icon={<CheckCircleOutlined />} style={{ marginInlineEnd: 0 }}>
                Transferred
              </Tag>
            </Tooltip>
          ) : (
            <Tooltip title="Transfer Lead">
              <Button
                type="text"
                icon={<UserSwitchOutlined />}
                onClick={(event) => {
                  event.stopPropagation();
                  openRecover(record);
                }}
              />
            </Tooltip>
          )}
          <Tooltip title="Delete">
            <Popconfirm
              title={
                record.recoveredLeadId
                  ? 'This Review Lead was transferred to a normal Lead. Deleting this Review Lead will not delete the transferred Lead. Continue?'
                  : 'Are you sure you want to permanently delete this Review Lead?'
              }
              okText="Delete"
              cancelText="Cancel"
              okType="danger"
              onConfirm={() => handleSingleDelete(record)}
            >
              <Button
                type="text"
                danger
                loading={deletingId === record._id}
                icon={<DeleteOutlined />}
                onClick={(event) => event.stopPropagation()}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <Space direction="vertical" size={2}>
          <Title level={2} style={{ margin: 0 }}>Review Leads</Title>
          <Text type="secondary">Rejected leads from phone validation</Text>
        </Space>

        <Space wrap>
          <RangePicker
            allowClear={false}
            presets={reviewLeadDateRangePresets}
            value={dateRange}
            onChange={handleDateRangeChange}
          />
          <Input
            allowClear
            placeholder="Search Review Leads"
            value={searchTerm}
            suffix={<SearchOutlined />}
            onChange={(event) => {
              setCurrentPage(1);
              setSearchTerm(event.target.value);
            }}
            style={{ width: 220 }}
          />
          <Select
            allowClear
            placeholder="Reason"
            value={reasonCode}
            onChange={(value) => {
              setCurrentPage(1);
              setReasonCode(value);
            }}
            options={reasonOptions.map((value) => ({ value, label: value }))}
            style={{ width: 210 }}
          />
          <Select
            placeholder="Transfer Status"
            value={transferStatus}
            onChange={(value) => {
              setCurrentPage(1);
              setTransferStatus(value || 'all');
            }}
            options={[
              { value: 'all', label: 'All' },
              { value: 'transferred', label: 'Transferred' },
              { value: 'notTransferred', label: 'Not Transferred' }
            ]}
            style={{ width: 170 }}
          />
          {selectedRowKeys.length > 0 && (
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={bulkDeleting}
              onClick={confirmBulkDelete}
            >
              Delete Selected ({selectedRowKeys.length})
            </Button>
          )}
          <Button icon={<ReloadOutlined />} onClick={handleRefreshReviewLeads} />
        </Space>
      </div>

      <div ref={tableShellRef} style={reviewTableShellStyle}>
        <div style={reviewTableScrollControlsStyle}>
          {tableScrollState.canScrollLeft ? (
            <Tooltip title="Scroll table left">
              <Button
                aria-label="Scroll table left"
                icon={<LeftOutlined />}
                shape="circle"
                style={reviewTableScrollButtonStyle}
                onClick={(event) => {
                  event.stopPropagation();
                  scrollTableHorizontally('left');
                }}
              />
            </Tooltip>
          ) : (
            <span />
          )}

          {tableScrollState.canScrollRight ? (
            <Tooltip title="Scroll table right">
              <Button
                aria-label="Scroll table right"
                icon={<RightOutlined />}
                shape="circle"
                type="primary"
                style={reviewTableScrollButtonStyle}
                onClick={(event) => {
                  event.stopPropagation();
                  scrollTableHorizontally('right');
                }}
              />
            </Tooltip>
          ) : (
            <span />
          )}
        </div>

        <Table
          rowKey={(record) => record._id}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys
          }}
          columns={columns}
          dataSource={reviewLeads}
          loading={loading}
          locale={{
            emptyText: <Empty description="No Review Leads found" />
          }}
          pagination={{
            current: currentPage,
            pageSize,
            total: totalRecords,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '30', '50', '100'],
            showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} entries`
          }}
          onChange={(pagination) => {
            setCurrentPage(pagination.current);
            setPageSize(pagination.pageSize);
          }}
          scroll={{ x: 'max-content' }}
        />
      </div>

      <Modal
        title="Review Lead Details"
        open={detailsOpen}
        onCancel={() => {
          setDetailsOpen(false);
          setSelectedReviewLead(null);
        }}
        footer={(
          <Button onClick={() => {
            setDetailsOpen(false);
            setSelectedReviewLead(null);
          }}>
            Close
          </Button>
        )}
        width={860}
      >
        {detailsLoading ? (
          <Empty description="Loading details..." />
        ) : selectedReviewLead ? (
          <Space direction="vertical" size={18} style={{ width: '100%' }}>
            <div>
              <Title level={5} style={{ marginTop: 0 }}>Customer Info</Title>
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="Name">
                  {selectedReviewLead.personalInfo?.fullName || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  {selectedReviewLead.personalInfo?.email || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Phone">
                  {selectedReviewLead.personalInfo?.phoneNumber || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="ZIP">
                  {selectedReviewLead.personalInfo?.zipCode || 'N/A'}
                </Descriptions.Item>
              </Descriptions>
            </div>

            <div>
              <Title level={5}>Vehicle Info</Title>
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="Year">
                  {selectedReviewLead.vehicleInfo?.year || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Make">
                  {selectedReviewLead.vehicleInfo?.make || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Model">
                  {selectedReviewLead.vehicleInfo?.model || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Part">
                  {selectedReviewLead.vehicleInfo?.part || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="VIN" span={2}>
                  {selectedReviewLead.vehicleInfo?.vin || selectedReviewLead.vehicleInfo?.vinNumber || 'N/A'}
                </Descriptions.Item>
              </Descriptions>
            </div>

            <div>
              <Title level={5}>Rejection Details</Title>
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="Reason Code">
                  <Tag color="red">{selectedReviewLead.reasonCode || 'N/A'}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Stage">
                  {selectedReviewLead.rejectionStage || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Reason" span={2}>
                  {selectedReviewLead.reason || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Source">
                  {selectedReviewLead.source || 'unknown'}
                </Descriptions.Item>
                <Descriptions.Item label="Created">
                  {formatDate(selectedReviewLead.createdAt)}
                </Descriptions.Item>
              </Descriptions>
            </div>

            <div>
              <Title level={5}>Phone Validation</Title>
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="Raw Phone">
                  {selectedReviewLead.phoneValidation?.rawPhone || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Normalized E164">
                  {selectedReviewLead.phoneValidation?.normalizedE164 || 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Checked">
                  {formatDate(selectedReviewLead.phoneValidation?.checkedAt)}
                </Descriptions.Item>
              </Descriptions>
            </div>

            {selectedReviewLead.recoveredLeadId && (
              <Descriptions bordered size="small" column={2}>
                <Descriptions.Item label="Review Status">
                  <Tag color="green">{selectedReviewLead.reviewStatus || 'assigned'}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Recovered Lead ID">
                  {selectedReviewLead.recoveredLeadId}
                </Descriptions.Item>
              </Descriptions>
            )}
          </Space>
        ) : (
          <Empty description="No details available" />
        )}
      </Modal>

      <Modal
        title="Recover / Assign Review Lead"
        open={recoverOpen}
        onCancel={() => {
          setRecoverOpen(false);
          setSelectedReviewLead(null);
          recoverForm.resetFields();
        }}
        onOk={() => recoverForm.submit()}
        okText="Recover & Assign"
        confirmLoading={recoverLoading}
        width={560}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {selectedReviewLead && (
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="Customer">
                {selectedReviewLead.personalInfo?.fullName || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {selectedReviewLead.personalInfo?.phoneNumber || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Reason">
                {selectedReviewLead.reasonCode || 'N/A'}
              </Descriptions.Item>
            </Descriptions>
          )}

          <Form
            form={recoverForm}
            layout="vertical"
            onFinish={submitRecovery}
          >
            <Form.Item
              name="targetAgentId"
              label="Sales Agent"
              rules={[{ required: true, message: 'Please select a Sales Agent.' }]}
            >
              <Select
                showSearch
                loading={agentsLoading}
                placeholder="Select Sales Agent"
                optionFilterProp="label"
                options={salesAgents.map((agent) => ({
                  value: agent.value,
                  label: `${agent.label}${agent.subRole ? ` (${agent.subRole})` : ''}${agent.email ? ` - ${agent.email}` : ''}`
                }))}
              />
            </Form.Item>

            <Form.Item
              name="reason"
              label="Reason"
            >
              <Input.TextArea
                rows={3}
                maxLength={500}
                showCount
                placeholder="Optional recovery note"
              />
            </Form.Item>
          </Form>
        </Space>
      </Modal>

      <LeadFullDetailModal
        open={!!fullDetailLeadId}
        onClose={() => setFullDetailLeadId(null)}
        leadId={fullDetailLeadId}
        source="reviewLead"
      />
    </div>
  );
}

export default ReviewLeads;
